import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Plus, X, Calendar, Clock, Tag,
  Building2, RefreshCw, Trash2, Link2, LayoutGrid, List,
  CalendarDays, Activity, Filter, AlertTriangle, AlertCircle,
  CheckCircle2, Circle, Zap, Flag, MapPin, AlignLeft, Bell, ArrowLeft
} from "lucide-react";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const DAYS_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const EVENT_TYPES = [
  { value: "call",             label: "Call",              color: "#0A84FF" },
  { value: "meeting",          label: "Meeting",           color: "#AF52DE" },
  { value: "showing",          label: "Showing",           color: "#30D158" },
  { value: "inspection",       label: "Inspection",        color: "#0A84FF" },
  { value: "appraisal",        label: "Appraisal",         color: "#64D2FF" },
  { value: "closing",          label: "Closing Date",      color: "#D4AF37" },
  { value: "funding",          label: "Funding",           color: "#00FF88" },
  { value: "deadline",         label: "Deadline",          color: "#FF3B30" },
  { value: "contractor_visit", label: "Contractor Visit",  color: "#FF9F0A" },
  { value: "draw_request",     label: "Draw Request",      color: "#FF6B2B" },
  { value: "document_due",     label: "Document Due",      color: "#BF5AF2" },
  { value: "follow_up",        label: "Follow-Up",         color: "#30D158" },
  { value: "milestone",        label: "Milestone",         color: "#D4AF37" },
  { value: "custom",           label: "Other",             color: "#8E8E93" },
];

const MILESTONE_STATUS_CONFIG = {
  not_started: { color: "#3A3A3C", label: "Not Started" },
  upcoming:    { color: "#0A84FF", label: "Upcoming" },
  active:      { color: "#D4AF37", label: "Active" },
  completed:   { color: "#00FF88", label: "Completed" },
  overdue:     { color: "#FF3B30", label: "Overdue" },
  blocked:     { color: "#FF9F0A", label: "Blocked" },
  canceled:    { color: "#8E8E93", label: "Canceled" },
};

// ── Helpers ────────────────────────────────────────────────────────────

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }
function toISO(y, m, d) { return `${y}-${String(m + 1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }
function getWeekStart(d) { const s = new Date(d); s.setDate(d.getDate() - d.getDay()); return s; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function fmtDate(d) { return d.toISOString().slice(0, 10); }

function getEventColor(ev) {
  if (ev.color) return ev.color;
  const t = EVENT_TYPES.find(t => t.value === ev.type || t.value === ev.event_type);
  return t?.color || "#8E8E93";
}

// ── Event Pill ─────────────────────────────────────────────────────────

function EventPill({ event, onClick }) {
  const color = getEventColor(event);
  return (
    <div
      onClick={e => { e.stopPropagation(); onClick(event); }}
      className="truncate text-[9px] px-1 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity leading-tight"
      style={{ background: `${color}20`, color, borderLeft: `2px solid ${color}` }}
      title={event.title}
    >
      {event.title}
    </div>
  );
}

// ── Create Event Modal ─────────────────────────────────────────────────

function CreateEventModal({ date: initialDate, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", type: "meeting", date: initialDate || "", time: "",
    description: "", location: "", all_day: true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.date) return toast.error("Date is required");
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/calendar/events`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, all_day: !form.time }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success("Event created!");
      onCreated(data.event);
      onClose();
    } catch { toast.error("Failed to create event"); }
    finally { setSaving(false); }
  };

  const typeConfig = EVENT_TYPES.find(t => t.value === form.type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0F0F0F] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold">New Event</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <input
          autoFocus value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Event title"
          data-testid="event-title-input"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Type</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white focus:outline-none"
              style={{ color: typeConfig?.color }}
              data-testid="event-type-select">
              {EVENT_TYPES.map(t => (
                <option key={t.value} value={t.value} style={{ color: t.color, background: "#111" }}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Date</label>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Time (opt)</label>
            <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Location (opt)</label>
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="Address or link"
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
          </div>
        </div>

        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
          placeholder="Description / notes (optional)"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-[#D4AF37]/40" />

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-white/[0.08] text-white/50 rounded-xl text-sm hover:text-white transition-all">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-event-btn"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-black font-bold rounded-xl text-sm disabled:opacity-50 transition-all">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Event
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Event Detail Modal ─────────────────────────────────────────────────

function EventDetailModal({ event, onClose, onDelete }) {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const color = getEventColor(event);
  const typeLabel = EVENT_TYPES.find(t => t.value === event.type || t.value === event.event_type)?.label || event.type;
  const isManual = event.source === "manual";

  const deleteEvent = async () => {
    setDeleting(true);
    try {
      await fetch(`${API}/api/calendar/events/${event.event_id}`, { method: "DELETE", credentials: "include" });
      toast.success("Deleted");
      onDelete(event.event_id);
      onClose();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0F0F0F] border border-white/[0.1] rounded-2xl p-6 w-full max-w-sm space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{typeLabel}</span>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div>
          <h3 className="text-white font-bold text-lg">{event.title}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-white/40">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{event.date}</span>
            {event.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{event.time}</span>}
            {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.location}</span>}
          </div>
        </div>

        {event.description && <p className="text-white/50 text-sm bg-white/[0.03] rounded-lg p-3">{event.description}</p>}

        {event.deal_id && (
          <button onClick={() => { navigate(`/deals/${event.deal_id}`); onClose(); }}
            className="flex items-center gap-2 text-[#D4AF37] text-sm hover:underline">
            <Building2 className="w-3.5 h-3.5" /> View Deal →
          </button>
        )}

        {event.source === "deal" && (
          <p className="text-[10px] text-white/20 flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Synced from deal — edit dates in the deal page
          </p>
        )}

        {isManual && (
          <button onClick={deleteEvent} disabled={deleting}
            className="w-full flex items-center justify-center gap-2 py-2 border border-[#FF3B30]/20 text-[#FF3B30] rounded-xl text-sm hover:bg-[#FF3B30]/10 transition-all disabled:opacity-50">
            {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete Event
          </button>
        )}
      </div>
    </div>
  );
}

// ── Alert Banner ───────────────────────────────────────────────────────

function AlertBanner({ alerts }) {
  const [show, setShow] = useState(true);
  if (!show || !alerts || alerts.total_alerts === 0) return null;
  return (
    <div className="bg-[#FF3B30]/[0.06] border border-[#FF3B30]/20 rounded-xl p-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-[#FF3B30] flex-shrink-0" />
        <div className="text-xs text-white/60">
          {alerts.overdue?.length > 0 && (
            <span className="text-[#FF3B30] font-semibold">{alerts.overdue.length} overdue</span>
          )}
          {alerts.overdue?.length > 0 && alerts.blocked?.length > 0 && <span className="text-white/20 mx-1">·</span>}
          {alerts.blocked?.length > 0 && (
            <span className="text-[#FF9F0A] font-semibold">{alerts.blocked.length} blocked</span>
          )}
          {alerts.closing_soon?.length > 0 && (
            <span className="text-[#D4AF37] font-semibold ml-2">{alerts.closing_soon.length} closing within 7 days</span>
          )}
        </div>
      </div>
      <button onClick={() => setShow(false)} className="text-white/20 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
}

// ── Month View ─────────────────────────────────────────────────────────

function MonthView({ year, month, events, onDayClick, onEventClick, todayISO }) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDayOfMonth(year, month);

  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-white/[0.04]">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-center text-[10px] text-white/30 font-semibold uppercase py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`e-${i}`} className="min-h-[80px] border-r border-b border-white/[0.03]" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
          const iso = toISO(year, month, d);
          const dayEvents = events.filter(e => e.date === iso);
          const isToday = iso === todayISO;
          const isWeekend = (firstDay + d - 1) % 7 === 0 || (firstDay + d - 1) % 7 === 6;
          return (
            <div
              key={d} onClick={() => onDayClick(iso)}
              data-testid={`cal-day-${iso}`}
              className={`min-h-[80px] p-1.5 border-r border-b border-white/[0.03] cursor-pointer hover:bg-white/[0.03] transition-colors ${isWeekend ? "bg-white/[0.01]" : ""}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mb-1 font-medium ${
                isToday ? "bg-[#D4AF37] text-black font-bold" : "text-white/50"
              }`}>{d}</div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map(ev => (
                  <EventPill key={ev.event_id} event={ev} onClick={onEventClick} />
                ))}
                {dayEvents.length > 3 && <p className="text-[9px] text-white/30 pl-1">+{dayEvents.length - 3} more</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week View ──────────────────────────────────────────────────────────

function WeekView({ weekStart, events, onDayClick, onEventClick, todayISO }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl overflow-hidden">
      <div className="grid grid-cols-7 border-b border-white/[0.06]">
        {days.map((d, i) => {
          const iso = fmtDate(d);
          const isToday = iso === todayISO;
          return (
            <div key={i} className={`py-3 text-center border-r border-white/[0.03] last:border-r-0 ${isToday ? "bg-[#D4AF37]/[0.06]" : ""}`}>
              <div className="text-[10px] text-white/30 uppercase">{DAYS_SHORT[d.getDay()]}</div>
              <div className={`text-lg font-bold mt-0.5 ${isToday ? "text-[#D4AF37]" : "text-white/70"}`}>
                {d.getDate()}
              </div>
              <div className="text-[9px] text-white/20">{MONTHS[d.getMonth()].slice(0,3)}</div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-7 min-h-[480px]">
        {days.map((d, i) => {
          const iso = fmtDate(d);
          const dayEvents = events.filter(e => e.date === iso);
          const isToday = iso === todayISO;
          return (
            <div key={i}
              onClick={() => onDayClick(iso)}
              data-testid={`week-day-${iso}`}
              className={`p-2 border-r border-b border-white/[0.03] last:border-r-0 cursor-pointer hover:bg-white/[0.02] transition-colors ${isToday ? "bg-[#D4AF37]/[0.03]" : ""}`}
            >
              <div className="space-y-1">
                {dayEvents.map(ev => {
                  const color = getEventColor(ev);
                  return (
                    <div
                      key={ev.event_id}
                      onClick={e => { e.stopPropagation(); onEventClick(ev); }}
                      className="p-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ background: `${color}15`, borderLeft: `3px solid ${color}` }}
                    >
                      <p className="text-[10px] font-semibold truncate" style={{ color }}>{ev.title}</p>
                      {ev.time && <p className="text-[9px] text-white/30 mt-0.5">{ev.time}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Agenda View ────────────────────────────────────────────────────────

function AgendaView({ events, onEventClick }) {
  const grouped = {};
  const sorted = [...events].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  sorted.forEach(ev => {
    const k = ev.date || "No Date";
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(ev);
  });

  const today = new Date().toISOString().slice(0, 10);
  const keys = Object.keys(grouped).filter(k => k >= today).slice(0, 60);

  if (keys.length === 0) {
    return (
      <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-12 text-center">
        <CalendarDays className="w-10 h-10 text-white/10 mx-auto mb-3" />
        <p className="text-white/30">No upcoming events</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {keys.map(dateKey => {
        const d = new Date(dateKey + "T12:00:00");
        const isToday = dateKey === today;
        return (
          <div key={dateKey} className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl overflow-hidden">
            <div className={`px-4 py-3 border-b border-white/[0.04] flex items-center gap-3 ${isToday ? "bg-[#D4AF37]/[0.06]" : ""}`}>
              <div className={`text-center w-10 ${isToday ? "text-[#D4AF37]" : "text-white/50"}`}>
                <div className="text-[10px] uppercase">{DAYS_SHORT[d.getDay()]}</div>
                <div className="text-xl font-bold">{d.getDate()}</div>
              </div>
              <div>
                <span className={`text-sm font-semibold ${isToday ? "text-[#D4AF37]" : "text-white/70"}`}>
                  {isToday ? "Today" : `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`}
                </span>
                <p className="text-[10px] text-white/20">{grouped[dateKey].length} event{grouped[dateKey].length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="divide-y divide-white/[0.03]">
              {grouped[dateKey].map(ev => {
                const color = getEventColor(ev);
                const typeLabel = EVENT_TYPES.find(t => t.value === ev.type || t.value === ev.event_type)?.label || ev.type;
                return (
                  <button key={ev.event_id} onClick={() => onEventClick(ev)}
                    data-testid={`agenda-item-${ev.event_id}`}
                    className="w-full text-left px-4 py-3 hover:bg-white/[0.02] transition-colors flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white/80">{ev.title}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ color, background: `${color}15` }}>{typeLabel}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-white/30">
                        {ev.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ev.time}</span>}
                        {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                        {ev.source === "deal" && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />Deal event</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Cross-Deal Timeline View ───────────────────────────────────────────

function CrossDealTimelineView({ onNavigateDeal }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowStart, setWindowStart] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const WEEKS = 12;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/timeline/cross-deal`, { credentials: "include" });
      const d = await res.json();
      setData(d.timeline || []);
    } catch { toast.error("Failed to load timeline"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalDays = WEEKS * 7;
  const windowEnd = addDays(windowStart, totalDays);
  const wStartISO = fmtDate(windowStart);
  const wEndISO = fmtDate(windowEnd);

  const weeks = Array.from({ length: WEEKS }, (_, i) => addDays(windowStart, i * 7));
  const today = fmtDate(new Date());
  const todayOffset = Math.max(0, (new Date(today) - windowStart) / (1000 * 60 * 60 * 24));
  const todayPct = Math.min(100, (todayOffset / totalDays) * 100);

  if (loading) return (
    <div className="flex justify-center items-center py-20">
      <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
    </div>
  );

  if (data.length === 0) return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-12 text-center">
      <Activity className="w-10 h-10 text-white/10 mx-auto mb-3" />
      <p className="text-white/30 text-sm">No deal timelines yet.</p>
      <p className="text-white/20 text-xs mt-1">Add deals and advance their stage to auto-generate milestones.</p>
    </div>
  );

  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl overflow-hidden">
      {/* Header controls */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-sm font-bold text-white/70">Cross-Deal Milestone Timeline</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setWindowStart(d => addDays(d, -28))}
            className="p-1.5 hover:bg-white/[0.06] rounded-lg transition-colors">
            <ChevronLeft className="w-4 h-4 text-white/40" />
          </button>
          <span className="text-xs text-white/40">
            {MONTHS[windowStart.getMonth()].slice(0,3)} {windowStart.getFullYear()}
          </span>
          <button onClick={() => setWindowStart(d => addDays(d, 28))}
            className="p-1.5 hover:bg-white/[0.06] rounded-lg transition-colors">
            <ChevronRight className="w-4 h-4 text-white/40" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: "900px" }}>
          {/* Week header */}
          <div className="flex border-b border-white/[0.04]">
            <div className="w-48 flex-shrink-0 px-3 py-2 text-[10px] text-white/20 uppercase tracking-wider">Deal</div>
            <div className="flex-1 relative">
              <div className="flex">
                {weeks.map((w, i) => (
                  <div key={i} className="flex-1 text-center text-[9px] text-white/20 py-2 border-l border-white/[0.03]">
                    {MONTHS[w.getMonth()].slice(0,3)} {w.getDate()}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deal rows */}
          {data.map(({ deal, milestones }) => {
            const msInWindow = milestones.filter(m => {
              if (!m.due_date) return false;
              return m.due_date >= wStartISO && m.due_date <= wEndISO;
            });

            return (
              <div key={deal.deal_id} className="flex items-center border-b border-white/[0.03] hover:bg-white/[0.02] group min-h-[52px]">
                <div className="w-48 flex-shrink-0 px-3 py-3">
                  <button
                    onClick={() => onNavigateDeal(deal.deal_id)}
                    className="text-left group-hover:text-[#D4AF37] transition-colors"
                  >
                    <p className="text-xs font-medium text-white/70 truncate leading-tight">{deal.property_address || "Unknown"}</p>
                    <p className="text-[10px] text-white/20 mt-0.5">{deal.pipeline_stage?.replace(/_/g, " ")}</p>
                  </button>
                </div>

                <div className="flex-1 relative py-3 px-1">
                  {/* Today marker */}
                  {todayPct >= 0 && todayPct <= 100 && (
                    <div
                      className="absolute top-0 bottom-0 w-px bg-[#D4AF37]/40 z-10"
                      style={{ left: `${todayPct}%` }}
                    />
                  )}

                  <div className="relative h-6 flex items-center">
                    {msInWindow.map(m => {
                      const offsetDays = Math.max(0, (new Date(m.due_date) - windowStart) / (1000 * 60 * 60 * 24));
                      const pct = Math.min(99, (offsetDays / totalDays) * 100);
                      const scfg = MILESTONE_STATUS_CONFIG[m.status] || { color: "#8E8E93" };
                      return (
                        <div
                          key={m.milestone_id}
                          className="absolute flex flex-col items-center z-20"
                          style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
                          title={`${m.title} — ${m.due_date} (${scfg.label})`}
                        >
                          <div
                            className="w-3 h-3 rounded-full border-2 cursor-pointer hover:scale-150 transition-transform"
                            style={{ background: scfg.color, borderColor: scfg.color }}
                          />
                          <span className="text-[8px] mt-0.5 whitespace-nowrap" style={{ color: scfg.color }}>
                            {m.title.slice(0, 10)}
                          </span>
                        </div>
                      );
                    })}
                    {msInWindow.length === 0 && (
                      <div className="w-full h-px bg-white/[0.04]" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-white/[0.04] flex flex-wrap gap-3">
        {Object.entries(MILESTONE_STATUS_CONFIG).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />
            <span className="text-[10px] text-white/30">{v.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <div className="w-px h-3 bg-[#D4AF37]/40" />
          <span className="text-[10px] text-white/20">Today</span>
        </div>
      </div>
    </div>
  );
}

// ── Right sidebar (upcoming + legend) ─────────────────────────────────

function UpcomingPanel({ events, onEventClick }) {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = [...events]
    .filter(e => e.date >= today)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4 space-y-3">
        <h3 className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Upcoming</h3>
        {upcoming.length === 0 ? (
          <p className="text-white/20 text-xs py-3 text-center">No upcoming events</p>
        ) : upcoming.map(ev => {
          const color = getEventColor(ev);
          return (
            <button key={ev.event_id} onClick={() => onEventClick(ev)}
              data-testid={`sidebar-event-${ev.event_id}`}
              className="w-full text-left p-3 bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.04] rounded-xl transition-all group">
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-white/80 text-xs font-medium truncate group-hover:text-white">{ev.title}</p>
                  <p className="text-white/30 text-[10px] mt-0.5">{ev.date}{ev.time ? ` · ${ev.time}` : ""}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4 space-y-2">
        <h3 className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Event Types</h3>
        {EVENT_TYPES.map(t => (
          <div key={t.value} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: t.color }} />
            <span className="text-[11px] text-white/40">{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── View Tabs ──────────────────────────────────────────────────────────

const VIEWS = [
  { id: "month",    label: "Month",    Icon: LayoutGrid },
  { id: "week",     label: "Week",     Icon: CalendarDays },
  { id: "agenda",   label: "Agenda",   Icon: List },
  { id: "timeline", label: "Timeline", Icon: Activity },
];

// ── Main Page ──────────────────────────────────────────────────────────

export default function CalendarPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const portalFrom = searchParams.get("from"); // e.g. "lender", "contractor", "realtor", "title"
  const PORTAL_BACK = {
    lender: { label: "Lender Portal", url: "/lender/portal" },
    contractor: { label: "Contractor Portal", url: "/contractor/dashboard" },
    realtor: { label: "Realtor Portal", url: "/realtor/dashboard" },
    title: { label: "Title Portal", url: "/title/dashboard" },
  };
  const backPortal = portalFrom ? PORTAL_BACK[portalFrom] : null;
  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);

  const [view, setView]     = useState("month");
  const [year, setYear]     = useState(today.getFullYear());
  const [month, setMonth]   = useState(today.getMonth());
  const [weekStart, setWeekStart] = useState(() => getWeekStart(today));

  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [alerts, setAlerts]     = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreate, setShowCreate]     = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/calendar/events?month=${month + 1}&year=${year}`, { credentials: "include" });
      const data = await res.json();
      setEvents(data.events || []);
    } catch { toast.error("Failed to load events"); }
    finally { setLoading(false); }
  }, [month, year]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/timeline/alerts/summary`, { credentials: "include" });
      if (res.ok) setAlerts(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const prevPeriod = () => {
    if (view === "month") {
      if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
    } else if (view === "week") {
      setWeekStart(d => addDays(d, -7));
    }
  };
  const nextPeriod = () => {
    if (view === "month") {
      if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
    } else if (view === "week") {
      setWeekStart(d => addDays(d, 7));
    }
  };

  const handleDayClick = (iso) => { setSelectedDate(iso); setShowCreate(true); };
  const handleEventCreated = (ev) => setEvents(prev => [...prev, ev].sort((a, b) => (a.date||"").localeCompare(b.date||"")));
  const handleEventDeleted = (id) => setEvents(prev => prev.filter(e => e.event_id !== id));

  const headerLabel = view === "week"
    ? `${MONTHS[weekStart.getMonth()].slice(0,3)} ${weekStart.getDate()} – ${MONTHS[addDays(weekStart, 6).getMonth()].slice(0,3)} ${addDays(weekStart, 6).getDate()}, ${weekStart.getFullYear()}`
    : `${MONTHS[month]} ${year}`;

  const showNav = view === "month" || view === "week";

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Only show investor sidebar when NOT coming from a portal */}
      {!portalFrom && <Sidebar user={user} subscription={subscription} onLogout={onLogout} />}

      {/* Portal top bar — shown when coming from a partner portal */}
      {portalFrom && backPortal && (
        <div className="fixed top-0 left-0 right-0 h-14 bg-black border-b border-white/[0.06] flex items-center px-4 z-40">
          <Link to={backPortal.url} className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to {backPortal.label}
          </Link>
        </div>
      )}

      <main className={portalFrom ? "pt-14 px-4 sm:px-6 py-4 sm:py-6 max-w-7xl mx-auto" : mainClass}>
        <div className={`${portalFrom ? "" : "px-4 sm:px-6 py-4 sm:py-6 max-w-7xl mx-auto"} space-y-5`}>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-[#D4AF37]" /> Deal Calendar
              </h1>
              <p className="text-white/40 text-sm mt-0.5">Execution timeline, milestones, and critical events — all in one place</p>
            </div>
            <button
              onClick={() => { setSelectedDate(todayISO); setShowCreate(true); }}
              data-testid="new-event-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold rounded-xl text-sm transition-all self-start"
            >
              <Plus className="w-4 h-4" /> New Event
            </button>
          </div>

          {/* Smart Alerts */}
          <AlertBanner alerts={alerts} />

          {/* View selector + nav */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-1 bg-black/30 border border-white/[0.06] rounded-xl p-1">
              {VIEWS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  data-testid={`view-${id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    view === id ? "bg-[#D4AF37] text-black" : "text-white/40 hover:text-white"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            {showNav && (
              <div className="flex items-center gap-3">
                <button onClick={prevPeriod} className="p-1.5 hover:bg-white/[0.06] rounded-lg transition-colors">
                  <ChevronLeft className="w-4 h-4 text-white/60" />
                </button>
                <h2 className="text-white font-bold text-sm min-w-[160px] text-center">{headerLabel}</h2>
                <button onClick={nextPeriod} className="p-1.5 hover:bg-white/[0.06] rounded-lg transition-colors">
                  <ChevronRight className="w-4 h-4 text-white/60" />
                </button>
              </div>
            )}
          </div>

          {/* Content */}
          {view === "timeline" ? (
            <CrossDealTimelineView onNavigateDeal={(id) => navigate(`/deals/${id}`)} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
              <div className="lg:col-span-3">
                {loading ? (
                  <div className="flex justify-center items-center py-20 bg-[#0D0D0D] border border-white/[0.06] rounded-2xl">
                    <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
                  </div>
                ) : view === "month" ? (
                  <MonthView year={year} month={month} events={events}
                    onDayClick={handleDayClick} onEventClick={setSelectedEvent} todayISO={todayISO} />
                ) : view === "week" ? (
                  <WeekView weekStart={weekStart} events={events}
                    onDayClick={handleDayClick} onEventClick={setSelectedEvent} todayISO={todayISO} />
                ) : (
                  <AgendaView events={events} onEventClick={setSelectedEvent} />
                )}
              </div>
              <div>
                <UpcomingPanel events={events} onEventClick={setSelectedEvent} />
              </div>
            </div>
          )}
        </div>
      </main>

      {showCreate && (
        <CreateEventModal
          date={selectedDate}
          onClose={() => setShowCreate(false)}
          onCreated={handleEventCreated}
        />
      )}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onDelete={handleEventDeleted}
        />
      )}
    </div>
  );
}
